import { Service, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxOption, OnyxOptional } from '@onyx/angular';
import { OnyxDatePipe, OnyxLanguagePipe } from '@onyx/angular';
import { chain } from 'lodash';
import { DateTime } from 'luxon';
import { DOCUMENT_EXPIRATION_WARNING_THRESHOLD } from '../../../../common/constants/common/document-expiration-warning-threshold';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationSeverity } from '../../../../common/enums/validation-severity';
import { ColorHelper } from '../../../../common/helpers/color.helper';
import {
  ValidationConditionsHelper,
  ValidationContext,
} from '../../../../common/helpers/validation-conditions.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { ValidationIssue } from '../../../../common/interfaces/validation/validation-issue';
import type { ValidationResult } from '../../../../common/interfaces/validation/validation-result';
import { FleetIdentifierPipe } from '../../../../common/pipes/fleet-identifier.pipe';
import { JoinPipe } from '../../../../common/pipes/join.pipe';
import type { Fleet } from '../../../fleet/common/interfaces/fleet';
import { SwapSetAction } from '../../../map/common/enums/swap/swap-set-action';
import type { SwapSetData } from '../../../map/common/interfaces/swap/swap-set';
import { OrderModalHelper } from '../../../orders/common/helpers/order-modal.helper';
import type { Order } from '../../../orders/common/interfaces/order';
import type { Driver } from '../interfaces/driver';
import { DriverHelper } from './driver.helper';

@Service()
export class DriverValidationHelper {
  private readonly translateService = inject(TranslateService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly validationConditionsHelper = inject(
    ValidationConditionsHelper,
  );
  private readonly orderModalHelper = inject(OrderModalHelper);

  private readonly I18N = 'issues';

  public validate(data: {
    context: ValidationContext;
    driver: Driver;
    options: {
      vehicle?: Fleet;
      trailer?: Fleet;
      activeOrders?: SwapSetData<Order>[];
      drivers?: Driver[];
      option?: OnyxOption<Driver>;
    };
  }): ValidationResult<Driver> {
    const { driver, options, context } = data;
    const { vehicle, option, trailer, activeOrders, drivers } = options;
    const issues: ValidationIssue[] = [];

    this.validationConditionsHelper.checkSkills(
      driver,
      issues,
      vehicle,
      trailer,
    );
    this.checkUnavailability(driver, issues);
    this.validationConditionsHelper.checkAdr(driver, issues, vehicle, trailer);
    this.validationConditionsHelper.checkCountries(context, issues, {
      driver,
      vehicle,
      trailer,
    });
    this.checkContractEnd(context, driver, issues);
    this.checkVisas(driver, issues, true);
    this.checkDocuments(context, driver, issues, []);
    this.checkPermissions(context, driver, issues);
    this.validationConditionsHelper.checkRequiredDrivingLicense(
      context,
      driver,
      issues,
      { vehicle, trailer },
    );
    this.validationConditionsHelper.checkDrivingLicenseExpiryDate(
      context,
      driver,
      issues,
      { vehicle, trailer },
    );

    if (activeOrders?.length) {
      this.checkOrderIssues(driver, issues, activeOrders, drivers);
    }

    if (context !== ValidationContext.SWAP_PANEL) {
      this.validationConditionsHelper.checkActiveDelegation(driver, issues);
      this.checkAssignedDriver(driver, issues, vehicle);
    }

    return ValidationHelper.mapIssues(issues, option);
  }

  public validateIntegrationDriver({
    driver,
    ignoreDriver,
    option,
  }: {
    driver: Driver;
    ignoreDriver?: string | null;
    option?: OnyxOption<Driver>;
  }): ValidationResult<Driver> {
    const issues: ValidationIssue[] = [];

    if (ignoreDriver !== driver.uuid) {
      this.checkHasTelematics(driver, issues);
    }

    return ValidationHelper.mapIssues(issues, option);
  }

  public checkCellDocuments(driver: Driver, issues: ValidationIssue[]): void {
    this.checkDocuments(ValidationContext.DRIVER, driver, issues, [
      {
        key: 'professionalQualifications',
        expirationDate:
          driver.driversLicenseAndProfessionalQualifications
            .professionalQualificationsExpirationDate,
      },
    ]);
    this.checkVisas(driver, issues, false);
    this.checkPermissions(ValidationContext.DRIVER, driver, issues);
  }

  private checkDocuments(
    context: ValidationContext,
    driver: Driver,
    issues: ValidationIssue[],
    additionalDocuments: {
      key: string;
      expirationDate: OnyxOptional<string>;
    }[],
  ): void {
    const documentStatuses = this.validationConditionsHelper.getExpiryStatuses(
      [
        ...DriverHelper.getDriverExpiryDocuments(driver),
        ...additionalDocuments,
      ],
      (status) => status.expirationDate,
      ColorHelper.getCellColor,
    );
    if (!documentStatuses) return;

    const checkDocumentByStatus = (
      items: typeof documentStatuses.errors | typeof documentStatuses.warnings,
      single: string,
      multiple: string,
    ) =>
      this.validationConditionsHelper.addExpiryIssue(
        items,
        `${this.I18N}.documents.${single}`,
        `${this.I18N}.documents.${multiple}`,
        issues,
        {
          ...(context === ValidationContext.SWAP_PANEL && {
            forceSeverity: ValidationSeverity.WARNING,
          }),
          getData: (item) => ({
            document: this.translateService.instant(`labels.${item.key}`),
            date: this.datePipe.transform(item.expirationDate!, 'date-dot'),
          }),
        },
      );

    checkDocumentByStatus(
      documentStatuses.errors,
      'documentExpired',
      'documentsExpired',
    );
    checkDocumentByStatus(
      documentStatuses.warnings,
      'documentExpires',
      'documentsExpires',
    );
  }

  private checkAssignedDriver(
    driver: Driver,
    issues: ValidationIssue[],
    vehicle?: Fleet,
  ): void {
    if (
      vehicle &&
      driver.assignedVehicle &&
      driver.assignedVehicle.uuid !== vehicle.uuid
    ) {
      issues.push({
        message: `${this.I18N}.driverIsAssigned`,
        data: {
          vehicle: this.fleetIdentifierPipe.transform(driver.assignedVehicle),
        },
        severity: ValidationSeverity.WARNING,
      });
    }
  }

  private checkUnavailability(driver: Driver, issues: ValidationIssue[]): void {
    if (!driver.unavailability) return;

    issues.push({
      message: `${this.I18N}.driverUnavailable`,
      data: {
        date: this.datePipe.transform(
          driver.unavailability.dateRange.to!,
          'date-dot',
        ),
      },
      severity: ValidationSeverity.WARNING,
    });
  }

  private checkContractEnd(
    context: ValidationContext,
    driver: Driver,
    issues: ValidationIssue[],
  ): void {
    const contractEnd = driver.employmentConditions.contractTerm?.to;
    if (contractEnd) {
      const expiryDate = DateTime.fromISO(contractEnd);
      const days = expiryDate.diffNow('days').days;

      const message =
        days <= 0
          ? `${this.I18N}.contract.contractExpired`
          : days <= DOCUMENT_EXPIRATION_WARNING_THRESHOLD
            ? `${this.I18N}.contract.contractExpires`
            : null;
      if (!message) return;

      issues.push({
        message,
        data: { date: this.datePipe.transform(contractEnd, 'date-dot') },
        severity:
          context === ValidationContext.SWAP_PANEL
            ? ValidationSeverity.WARNING
            : days <= 0
              ? ValidationSeverity.ERROR
              : ValidationSeverity.WARNING,
      });
    }
  }

  private checkVisas(
    driver: Driver,
    issues: ValidationIssue[],
    force: boolean,
  ): void {
    const visaStatuses = this.validationConditionsHelper.getExpiryStatuses(
      driver.visas,
      (visa) => visa.expirationDate,
      ColorHelper.getCellColor,
    );
    if (!visaStatuses) return;

    const checkVisaByStatus = (
      items: typeof visaStatuses.errors | typeof visaStatuses.warnings,
      single: string,
      multiple: string,
    ) =>
      this.validationConditionsHelper.addExpiryIssue(
        items,
        `${this.I18N}.visas.${single}`,
        `${this.I18N}.visas.${multiple}`,
        issues,
        {
          ...(force && { forceSeverity: ValidationSeverity.WARNING }),
          getData: (item) => ({
            visa: this.translateService.instant(
              `${DictionaryCode.COUNTRY}.${item.country}`,
            ),
            date: this.datePipe.transform(item.expirationDate!, 'date-dot'),
          }),
          getListData: (items) => ({
            visas: this.joinPipe.transform(
              items.map((item) => item.country),
              { maxLength: 3, i18nKey: DictionaryCode.COUNTRY },
            ),
          }),
        },
      );

    checkVisaByStatus(visaStatuses.errors, 'visaExpired', 'visasExpired');
    checkVisaByStatus(visaStatuses.warnings, 'visaExpires', 'visasExpires');
  }

  private checkPermissions(
    context: ValidationContext,
    driver: Driver,
    issues: ValidationIssue[],
  ): void {
    const permissionStatuses = chain(driver.permissions)
      .filter((permission) => permission.expirationDate != null)
      .thru((permissions) =>
        this.validationConditionsHelper.getExpiryStatuses(
          permissions,
          (permission) => permission.expirationDate,
          ColorHelper.getCellColor,
        ),
      )
      .value();
    if (!permissionStatuses) return;

    const checkPermissionByStatus = (
      items:
        typeof permissionStatuses.errors | typeof permissionStatuses.warnings,
      single: string,
      multiple: string,
    ) =>
      this.validationConditionsHelper.addExpiryIssue(
        items,
        `${this.I18N}.permissions.${single}`,
        `${this.I18N}.permissions.${multiple}`,
        issues,
        {
          ...(context === ValidationContext.SWAP_PANEL && {
            forceSeverity: ValidationSeverity.WARNING,
          }),
          getData: (item) => ({
            permission: this.languagePipe.transform(item.type.names),
            date: this.datePipe.transform(item.expirationDate!, 'date-dot'),
          }),
          getListData: (items) => ({
            permissions: this.joinPipe.transform(
              items.map((item) => this.languagePipe.transform(item.type.names)),
              { maxLength: 3 },
            ),
          }),
        },
      );

    checkPermissionByStatus(
      permissionStatuses.errors,
      'permissionExpired',
      'permissionsExpired',
    );
    checkPermissionByStatus(
      permissionStatuses.warnings,
      'permissionExpires',
      'permissionsExpires',
    );
  }

  private checkOrderIssues(
    driver: Driver,
    issues: ValidationIssue[],
    activeOrders?: SwapSetData<Order>[],
    drivers?: Driver[],
  ): void {
    if (!activeOrders?.length) return;

    const validateData = drivers?.length ? drivers : [driver];
    const orderWarningsMap = new Map<string, Set<string>>();
    for (const { value: order, action } of activeOrders) {
      const isDropped = [
        SwapSetAction.UNLOAD_IN_POINT,
        SwapSetAction.DROP_OFF_ON_SEMI_TRAILER,
      ].includes(action);
      if (!order || isDropped) continue;

      const results = this.orderModalHelper.getSearchingDriversParameters(
        order,
        validateData,
      );

      for (const result of results) {
        if (result.ok()) continue;

        if (!orderWarningsMap.has(result.name)) {
          orderWarningsMap.set(result.name, new Set());
        }

        orderWarningsMap.get(result.name)!.add(order.identifier);
      }
    }

    for (const [key, identifiers] of orderWarningsMap) {
      issues.push({
        severity: ValidationSeverity.WARNING,
        message: `${this.translateService.instant(key)} (${Array.from(identifiers).join(', ')})`,
      });
    }
  }

  private checkHasTelematics(driver: Driver, issues: ValidationIssue[]): void {
    if (!driver.telematics) return;

    issues.push({
      severity: ValidationSeverity.WARNING,
      message: `${this.I18N}.driverAlreadyAssigned`,
    });
  }
}
